import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { EMPTY, catchError, finalize, first, map, Observable, tap } from 'rxjs';
import { DeActivatable } from 'src/app/core/interfaces/de-activatable.interface';
import { SupportSessionStatus } from 'src/app/core/models/platform-access/support-session-status.interface';
import { AuthorizedUserData } from 'src/app/core/models/user/auth-user.interface';
import { AuthService } from 'src/app/core/services/auth.service';
import { ForbiddenService } from 'src/app/core/services/forbidden.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TariffService } from 'src/app/core/services/tariff.service';

@Component({
    selector: 'app-content',
    templateUrl: './content.component.html',
    styleUrls: ['./content.component.scss'],
    standalone: false
})
export class ContentComponent implements DeActivatable, OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private authService = inject(AuthService);
  private forbiddenService = inject(ForbiddenService);
  private tariffService = inject(TariffService);

  public isMinimizedMenu = false;
  public dropdownMenu = false;
  public isForbidden$ = this.forbiddenService.forbidden$;
  public isSupportModeVisible = false;
  public supportModeCompanyName = '';
  public supportModeAccessLabelKey = '';
  public isSupportModeLoading = false;

  private readonly destroyRef = inject(DestroyRef);
  private currentCompanyId?: string;

  constructor() {
    this.router.events
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((e) => {
          if (e instanceof NavigationStart) {
            this.forbiddenService.hide();
          }
        }),
      )
      .subscribe();
  }

  ngOnInit() {
    this.authService.user$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((user) => this.syncSupportMode(user.data)),
      )
      .subscribe();

    this.route.params
      .pipe(
        first(),
        tap((params) => {
          this.currentCompanyId = params['id'];
          this.tariffService.setCompanyId(this.currentCompanyId!);
        }),
      )
      .subscribe();
  }

  canDeactivate(): boolean | Promise<boolean> | Observable<boolean> {
    const currentUrl = this.router.url;

    const companyUrlPattern = new RegExp(
      `^/companies/${this.currentCompanyId}(/|$)`,
    );

    if (!companyUrlPattern.test(currentUrl)) {
      const currentUser = this.authService.user$.getValue().data;

      if (currentUser?.supportCompanyId) {
        return this.authService.closeSupportSession().pipe(
          first(),
          tap(() => this.clearSupportModeBanner()),
          map(() => true),
        );
      }

      this.authService.generateJwtToken(this.currentCompanyId).pipe(first()).subscribe();
    }

    return true;
  }

  public onMenuSizeChanged(isMinimizedMenu: boolean): void {
    this.isMinimizedMenu = isMinimizedMenu;
  }

  public toggleMenu(): void {
    this.dropdownMenu = !this.dropdownMenu;
  }

  public onMenuItemSelected(): void {
    this.dropdownMenu = false;
  }

  public leaveSupportMode(): void {
    this.isSupportModeLoading = true;
    this.authService
      .closeSupportSession()
      .pipe(
        first(),
        tap(() => {
          this.clearSupportModeBanner();
          void this.router.navigateByUrl(this.authService.getCompaniesLandingUrl());
        }),
        catchError(() => EMPTY),
        finalize(() => {
          this.isSupportModeLoading = false;
        }),
      )
      .subscribe();
  }

  private syncSupportMode(userData?: AuthorizedUserData): void {
    if (!userData?.supportCompanyId || !this.authService.isSuperAdmin(userData)) {
      this.clearSupportModeBanner();
      return;
    }

    this.isSupportModeLoading = true;
    this.authService
      .getSupportSession()
      .pipe(
        first(),
        tap((status) => this.applySupportModeStatus(status)),
        catchError(() => {
          this.supportModeCompanyName = userData.supportCompanyId ?? '';
          this.supportModeAccessLabelKey = this.resolveAccessModeLabel(userData.supportAccessMode);
          this.isSupportModeVisible = true;
          return EMPTY;
        }),
        finalize(() => {
          this.isSupportModeLoading = false;
        }),
      )
      .subscribe();
  }

  private applySupportModeStatus(status: SupportSessionStatus): void {
    if (!status.isActive) {
      this.clearSupportModeBanner();
      return;
    }

    this.supportModeCompanyName = status.companyName ?? status.companyId ?? '';
    this.supportModeAccessLabelKey = this.resolveAccessModeLabel(status.accessMode);
    this.isSupportModeVisible = true;
    this.isSupportModeLoading = false;
  }

  private resolveAccessModeLabel(accessMode?: string): string {
    return accessMode === 'write'
      ? 'COMPONENTS.COMPANIES.SUPPORT_MODE_WRITE'
      : 'COMPONENTS.COMPANIES.SUPPORT_MODE_READ';
  }

  private clearSupportModeBanner(): void {
    this.isSupportModeVisible = false;
    this.isSupportModeLoading = false;
    this.supportModeCompanyName = '';
    this.supportModeAccessLabelKey = '';
  }
}
